/**
 * PRP-WEB-2 W2.3: the right-click menu. Opens at the pointer (kept on screen), focuses its first
 * item, and is keyboard-navigable: Up/Down move, Right/Enter open a submenu, Left/Escape close
 * it, Enter/Space run, Home/End jump, Tab closes. A click outside closes it. Items that cannot be
 * used show greyed with the reason as their title.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import type { MenuItem } from './menuModel';

export interface ContextMenuProps {
  x: number;
  y: number;
  title: string;
  items: MenuItem[];
  onClose: () => void;
}

export function ContextMenu({ x, y, title, items, onClose }: ContextMenuProps) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x, y });

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    setPos({ x: Math.max(4, Math.min(x, vw - r.width - 4)), y: Math.max(4, Math.min(y, vh - r.height - 4)) });
  }, [x, y]);

  useEffect(() => {
    const down = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) onClose(); };
    window.addEventListener('pointerdown', down, true);
    return () => window.removeEventListener('pointerdown', down, true);
  }, [onClose]);

  return (
    <div ref={box} className="fixed z-50" style={{ left: pos.x, top: pos.y }} onContextMenu={(e) => e.preventDefault()}>
      <MenuList title={title} items={items} onClose={onClose} onDone={onClose} autoFocus />
    </div>
  );
}

function MenuList({ title, items, onClose, onDone, autoFocus, sub }: {
  title: string; items: MenuItem[]; onClose: () => void; onDone: () => void; autoFocus?: boolean; sub?: boolean;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => { if (autoFocus) refs.current[0]?.focus(); }, [autoFocus]);

  const focus = (i: number) => {
    const n = items.length;
    refs.current[((i % n) + n) % n]?.focus();
  };

  const onKey = (i: number) => (e: KeyboardEvent) => {
    const it = items[i];
    if (e.key === 'ArrowDown') { e.preventDefault(); focus(i + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); focus(i - 1); }
    else if (e.key === 'Home') { e.preventDefault(); focus(0); }
    else if (e.key === 'End') { e.preventDefault(); focus(items.length - 1); }
    else if (e.key === 'ArrowRight' && it.items) { e.preventDefault(); setOpen(i); }
    else if ((e.key === 'ArrowLeft' && sub) || e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
    else if (e.key === 'Tab') { e.preventDefault(); onDone(); }
  };

  const activate = (i: number) => {
    const it = items[i];
    if (it.disabled) return;
    if (it.items) { setOpen(open === i ? null : i); return; }
    onDone();
    it.run?.();
  };

  return (
    <div role="menu" aria-label={title} data-testid={sub ? 'plan-submenu' : 'plan-menu'}
      className="min-w-[200px] max-w-[280px] rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-2)] py-1 text-[13px] text-[var(--tx-hi)] shadow-[0_10px_28px_rgba(0,0,0,.28)]">
      {!sub && <div className="px-3 pb-1 pt-1.5 text-[11px] font-[620] uppercase tracking-[.04em] text-[var(--tx-faint)]">{title}</div>}
      {items.map((it, i) => (
        <div key={it.id} className="relative">
          <button
            ref={(el) => { refs.current[i] = el; }}
            type="button"
            role="menuitem"
            data-testid={`menu-${it.id}`}
            aria-disabled={it.disabled ? true : undefined}
            aria-haspopup={it.items ? 'menu' : undefined}
            aria-expanded={it.items ? open === i : undefined}
            title={it.disabled}
            onKeyDown={onKey(i)}
            onClick={() => activate(i)}
            onMouseEnter={() => { refs.current[i]?.focus(); if (it.items && !it.disabled) setOpen(i); else setOpen(null); }}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left outline-none
              focus-visible:bg-[var(--bg-4)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent-line)] focus:bg-[var(--bg-4)]
              ${it.disabled ? 'cursor-default text-[var(--tx-faint)]' : it.danger ? 'text-[var(--bad)]' : ''}`}
          >
            <span className="flex-1 truncate">{it.label}</span>
            {it.items && <ChevronRight className="h-3.5 w-3.5 text-[var(--tx-lo)]" aria-hidden />}
          </button>
          {it.items && open === i && (
            <div className="absolute left-full top-0 z-10 ml-0.5 max-h-[60vh] overflow-y-auto">
              <MenuList title={it.label} items={it.items} sub autoFocus
                onClose={() => { setOpen(null); refs.current[i]?.focus(); }} onDone={onDone} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default ContextMenu;
